/**
 * Resolves a single entity to a minimal ``{ title }`` shape for use
 * by EntityChip. Keyed as ``["entity", kind, id]`` in the React Query
 * cache so results warm up automatically once fetched.
 */

import { useQuery } from "@tanstack/react-query";

import { notesApi } from "../api/notes";
import { tasksApi } from "../api/tasks";
import { calendarApi } from "../api/calendar";
import type { ApiClient } from "../api/client";
import { habitsApi } from "../api/habits";
import { recipesApi } from "../api/recipes";
import { shoppingApi } from "../api/shopping";
import { useApi } from "../auth/AuthProvider";
import type { MentionKind } from "./MentionContext";

const PREVIEW_MAX = 60;

interface EntityRef {
  title: string;
  starts_at?: string;
  next_occurrence_start?: string | null;
  next_occurrence_at?: string | null;
}

/**
 * Minimal event projection shared by ``useEntityRef``'s "event" case and
 * ``AppShell``'s mention-click navigation hint — both cache under the
 * same ``["entity", "event", id]`` key, so they share one fetch shape.
 */
export interface EventRef {
  title: string;
  starts_at: string;
  next_occurrence_start: string | null;
  next_occurrence_at: string | null;
}

export async function fetchEventRef(client: ApiClient, id: string): Promise<EventRef> {
  const ev = await calendarApi(client).get(id);
  return {
    title: ev.title,
    starts_at: ev.starts_at,
    next_occurrence_start: ev.next_occurrence_start,
    next_occurrence_at: ev.next_occurrence_at,
  };
}

export function useEntityRef(
  kind: MentionKind,
  id: string,
): { data: EntityRef | undefined; isLoading: boolean } {
  const client = useApi();

  const result = useQuery<EntityRef>({
    queryKey: ["entity", kind, id],
    queryFn: async () => {
      switch (kind) {
        case "note": {
          const note = await notesApi(client).get(id);
          const firstLine = note.content.split("\n")[0]?.trim().replace(/\s+/g, " ") ?? "";
          const preview = firstLine.length > PREVIEW_MAX
            ? `${firstLine.slice(0, PREVIEW_MAX)}…`
            : firstLine;
          return { title: preview || "(empty note)" };
        }
        case "task": {
          const task = await tasksApi(client).getFlat(id);
          return { title: task.title };
        }
        case "event":
          return await fetchEventRef(client, id);
        case "habit": {
          const habit = await habitsApi(client).get(id);
          return { title: habit.name };
        }
        case "shopping_list": {
          const lists = await shoppingApi(client).listLists();
          const found = lists.lists.find((l) => l.id === id);
          return { title: found?.name ?? "(unknown list)" };
        }
        case "shopping_item": {
          const item = await shoppingApi(client).getItem(id);
          return { title: item.name };
        }
        case "recipe": {
          const recipe = await recipesApi(client).get(id);
          return { title: recipe.title };
        }
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 min — entities don't change frequently
  });

  return { data: result.data, isLoading: result.isLoading };
}
